/** Everything the learner and the AI have said to each other, in one place.
 *
 * The client asked for it twice over: "are these questions recorded? History
 * of Questions needed of above ASK feature. Central place." and then, in
 * their own words, that the whole interaction between the user and the AI
 * should live under a tab next to the notebook.
 *
 * So it is ONE list, not two features side by side — a learner remembers
 * "I asked about the Dativ last week", not which product surface they were
 * on at the time. The filter is there for when they do remember.
 */
import { useEffect, useState } from "react";
import { freeSpeakingHistory } from "../../api/exams";
import type { FreeChatSession } from "../../api/exams";
import { deleteDoubt, listDoubts, readDoubt } from "../../api/tutor";
import type { DoubtMessage, DoubtThreadSummary } from "../../api/tutor";
import { ApiError } from "../../api/http";
import { Button, Card, EmptyState, Spinner } from "../../components/ui";
import { useToast } from "../../components/ToastContext";
import { useT } from "../../i18n";

type Kind = "doubt" | "speaking";
type Filter = "all" | Kind;

/** One entry in the merged list, whichever surface it came from. */
interface Entry {
  id: string;
  kind: Kind;
  title: string;
  at: string | null;
  count: number;
  /** Conversations arrive with their transcript; questions are fetched
   *  when opened, so the list stays small. */
  turns?: DoubtMessage[];
}

const TIME = (value: string | null) =>
  value ? new Date(value).toLocaleString("de-DE", {
    dateStyle: "medium", timeStyle: "short",
  }) : "";

function speakingTitle(s: FreeChatSession, fallback: string): string {
  return s.topic || [s.language, s.level].filter(Boolean).join(" · ") || fallback;
}

export function HistoryPage() {
  const t = useT();
  const { push } = useToast();
  const [entries, setEntries] = useState<Entry[] | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [openId, setOpenId] = useState<string | null>(null);
  const [turns, setTurns] = useState<DoubtMessage[] | null>(null);

  useEffect(() => {
    let live = true;
    // Both surfaces, in parallel. One failing must not blank the other —
    // a learner with no speaking sessions should still see their questions.
    Promise.allSettled([listDoubts(), freeSpeakingHistory()]).then(
      ([doubts, chats]) => {
        if (!live) return;
        const rows: Entry[] = [];
        if (doubts.status === "fulfilled") {
          doubts.value.forEach((d: DoubtThreadSummary) =>
            rows.push({
              id: `doubt:${d.id}`,
              kind: "doubt",
              title: d.title,
              at: d.last_message_at ?? d.created_at,
              count: d.message_count,
            }),
          );
        }
        if (chats.status === "fulfilled") {
          chats.value.sessions.forEach((s) =>
            rows.push({
              id: `speaking:${s.id}`,
              kind: "speaking",
              title: speakingTitle(s, t("history.conversation")),
              at: s.created_at,
              count: s.transcript?.length ?? 0,
              turns: (s.transcript ?? []).map((turn) => ({
                role: turn.role === "assistant" ? "tutor" : "learner",
                text: turn.text,
              })),
            }),
          );
        }
        rows.sort((a, b) => (b.at ?? "").localeCompare(a.at ?? ""));
        setEntries(rows);
      },
    );
    return () => {
      live = false;
    };
  }, [t]);

  const open = async (entry: Entry) => {
    if (openId === entry.id) {
      setOpenId(null);
      return;
    }
    setOpenId(entry.id);
    if (entry.turns) {
      setTurns(entry.turns);
      return;
    }
    setTurns(null);
    try {
      const thread = await readDoubt(entry.id.slice("doubt:".length));
      setTurns(thread.messages);
    } catch (err) {
      push("error", err instanceof ApiError ? err.message : t("common.error.generic"));
      setOpenId(null);
    }
  };

  const remove = async (entry: Entry) => {
    try {
      await deleteDoubt(entry.id.slice("doubt:".length));
      setEntries((prev) => (prev ?? []).filter((e) => e.id !== entry.id));
      if (openId === entry.id) setOpenId(null);
    } catch (err) {
      push("error", err instanceof ApiError ? err.message : t("common.error.generic"));
    }
  };

  const shown = (entries ?? []).filter(
    (e) => filter === "all" || e.kind === filter,
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold text-ink">{t("history.title")}</h1>
        <p className="mt-1 text-sm text-muted">{t("history.subtitle")}</p>
      </div>

      <div role="tablist" className="flex flex-wrap gap-2">
        {(["all", "doubt", "speaking"] as Filter[]).map((f) => (
          <button
            key={f}
            role="tab"
            aria-selected={filter === f}
            onClick={() => setFilter(f)}
            className={`rounded-control px-3 py-1.5 text-sm font-semibold ${
              filter === f ? "bg-brand text-white" : "text-muted hover:text-ink"
            }`}
          >
            {t(`history.filter.${f}`)}
          </button>
        ))}
      </div>

      {entries === null ? (
        <div className="grid place-items-center py-8">
          <Spinner />
        </div>
      ) : shown.length === 0 ? (
        <EmptyState message={t("history.empty")} />
      ) : (
        <ul className="space-y-3">
          {shown.map((entry) => (
            <li key={entry.id}>
              <Card>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <button
                    onClick={() => open(entry)}
                    aria-expanded={openId === entry.id}
                    className="min-w-0 flex-1 text-left"
                  >
                    <span className="block text-sm font-bold text-ink">
                      {entry.title}
                    </span>
                    <span className="mt-0.5 block text-xs text-muted">
                      {t(`history.kind.${entry.kind}`)} · {TIME(entry.at)} ·{" "}
                      {t("history.turns", { count: entry.count })}
                    </span>
                  </button>
                  {entry.kind === "doubt" && (
                    <Button
                      variant="ghost"
                      onClick={() => remove(entry)}
                      aria-label={t("history.delete")}
                    >
                      {t("history.delete")}
                    </Button>
                  )}
                </div>

                {openId === entry.id && (
                  turns === null ? (
                    <div className="mt-3 grid place-items-center py-4">
                      <Spinner size={18} />
                    </div>
                  ) : (
                    <ul className="mt-3 space-y-2">
                      {turns.map((turn, i) => (
                        <li
                          key={i}
                          className={`max-w-[90%] whitespace-pre-line rounded-control p-3 text-sm ${
                            turn.role === "tutor"
                              ? "bg-canvas text-ink"
                              : "ml-auto bg-brand-soft text-brand-dark"
                          }`}
                        >
                          <span className="block text-xs font-semibold text-muted">
                            {t(turn.role === "tutor" ? "history.ai" : "history.you")}
                          </span>
                          {turn.text}
                        </li>
                      ))}
                    </ul>
                  )
                )}
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
